import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { CreateChancel, ErrMessage, Input, SelectAutocompleteMultiple, TextareaCustom } from 'components';
import { FindErrorItem, FindLoad, FindSuccess, useModal } from 'utils';
import { httpRequestsOnErrorsActions, httpRequestsOnSuccessActions, uploadFile, wishlistActions } from 'store';
import { WishlistAttachmentDropzone, WishlistAttachmentList } from './wishlistAttachments';
import { WISHLIST_ATTACHMENT_PURPOSE, wishlistCategoryList } from '../constants';

const TITLE_MAX = 150;
const DESCRIPTION_MAX = 5000;
const MAX_ATTACHMENTS = 10;

/** Keep only the StoredFile fields the api accepts; `url` is minted per read and never sent back. */
const toStoredFile = ({ key, originalName, format, contentType, sizeBytes }) => ({
    key,
    originalName,
    format,
    contentType,
    sizeBytes,
});

/**
 * New Wishlist Request, and the edit form for an existing one.
 *
 * Org users set category, title, description and attachments. Status and
 * proposed completion date belong to TherapyLake and are not on this form —
 * the api rejects them from the tenant side anyway.
 *
 * Staged files upload to S3 only on submit, so abandoning the form leaves
 * nothing behind.
 *
 * @param {object}   info           the request being edited; omit to create
 * @param {object}   requestParams  list params the saga refetches with
 * @param {function} onCancel       edit mode: return to the details view instead of closing
 * @param {function} afterSuccess   called once the save lands
 */
export const WishlistForm = ({ info, requestParams, onCancel, afterSuccess, fromEdit }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const isEdit = !!info;
    const ACTION_TYPE = isEdit ? 'EDIT_WISHLIST_ITEM' : 'CREATE_WISHLIST_ITEM';
    const loader = FindLoad(ACTION_TYPE);
    const success = FindSuccess(ACTION_TYPE);
    const backError = FindErrorItem(ACTION_TYPE);
    const { handleSubmit, control } = useForm({
        defaultValues: {
            category: info?.category || null,
            title: info?.title || '',
            description: info?.description || '',
        },
    });

    const [savedFiles, setSavedFiles] = useState(info?.attachments || []);
    const [stagedFiles, setStagedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    useEffect(() => {
        if (success?.length) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(ACTION_TYPE));
            afterSuccess && afterSuccess();
            if (!isEdit) close();
        }
    }, [success]);

    useEffect(() => {
        return () => {
            dispatch(httpRequestsOnErrorsActions.removeError(ACTION_TYPE));
        };
    }, []);

    const total = savedFiles.length + stagedFiles.length;

    const handleAdd = (files) => {
        setUploadError('');
        const room = MAX_ATTACHMENTS - total;
        if (files.length > room) setUploadError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        setStagedFiles((prev) => [
            ...prev,
            ...files.slice(0, Math.max(room, 0)).map((file, i) => ({ localId: `${file.name}-${file.size}-${Date.now()}-${i}`, file })),
        ]);
    };

    const handleRemove = (f) => {
        if (f.localId) setStagedFiles((prev) => prev.filter((s) => s.localId !== f.localId));
        else setSavedFiles((prev) => prev.filter((s) => s.key !== f.key));
    };

    const submit = async (data) => {
        setUploadError('');
        let uploaded = [];
        if (stagedFiles.length) {
            setUploading(true);
            try {
                uploaded = await Promise.all(stagedFiles.map((s) => uploadFile(s.file, WISHLIST_ATTACHMENT_PURPOSE)));
            } catch (e) {
                setUploading(false);
                setUploadError('One of the files could not be uploaded. Please try again.');
                return;
            }
            setUploading(false);
        }

        const body = {
            category: data.category,
            title: data.title?.trim(),
            description: data.description?.trim(),
            attachments: [...savedFiles, ...uploaded].map(toStoredFile),
        };

        if (isEdit) {
            dispatch(wishlistActions.editWishlistItem(info.id, body, requestParams));
        } else {
            dispatch(wishlistActions.createWishlistItem(body, requestParams));
        }
    };

    const busy = uploading || !!loader?.length;

    return (
        <form onSubmit={handleSubmit(submit)} className="wishlist-form" style={{width: fromEdit ? 600 : 520}}>
            {!isEdit && <p className="modal-header-title">New Wishlist Request</p>}
            <div className="modal-body-wrapper" style={{ gap: 16, margin: fromEdit ? '0 0 12px' : '32px 0 12px' }}>
                <SelectAutocompleteMultiple
                    name="category"
                    label="Category*"
                    placeholder="Select Category"
                    control={control}
                    options={wishlistCategoryList}
                    renderValue={(option) => option?.name}
                    rules={{ required: 'Category is required.' }}
                />
                <Input
                    name="title"
                    label="Title*"
                    placeholder="Enter a short request title"
                    variant="outlined"
                    control={control}
                    rules={{
                        required: 'Title is required.',
                        validate: (v) => !!v?.trim() || 'Title is required.',
                    }}
                    maxLength={TITLE_MAX}
                />
                <TextareaCustom
                    name="description"
                    label="Description*"
                    // placeholder="Describe your request, issue, or idea..."
                    control={control}
                    rules={{
                        required: 'Description is required.',
                        validate: (v) => !!v?.trim() || 'Description is required.',
                    }}
                    maxLength={DESCRIPTION_MAX}
                    minRows={4}
                />

                {total > 0 && <p className="modal-header-sub">Attachments</p>}
                <WishlistAttachmentList files={[...savedFiles, ...stagedFiles]} onRemove={handleRemove} disabled={busy} />
                {total < MAX_ATTACHMENTS && <WishlistAttachmentDropzone onAdd={handleAdd} disabled={busy} />}

                {(uploadError || backError?.error) && <ErrMessage text={uploadError || backError?.error} />}
            </div>
            <CreateChancel
                type="submit"
                loader={busy}
                create={isEdit ? 'Save Changes' : 'Submit request'}
                chancel="Cancel"
                onClose={onCancel || close}
                buttonWidth="100%"
            />
        </form>
    );
};
